'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';

type EducationalStepsMessages = Record<string, { sections: Record<string, { title?: string; description: string }> }>;

type EducationalStepSection = {
  titleKey?: string;
  descriptionKey: string;
};

type UseEducationalTutorialConfig = {
  enableKeyboardNavigation?: boolean;
  enableScreenReaderAnnouncements?: boolean;
  enableFocusManagement?: boolean;
  onComplete?: () => void;
};

type UseEducationalTutorialReturn = {
  isOpen: boolean;
  currentStep: number;
  isLastStep: boolean;
  isFirstStep: boolean;
  stepKey: string;
  totalSteps: number;
  sections: EducationalStepSection[];

  nextButtonRef: RefObject<HTMLButtonElement | null>;
  previousButtonRef: RefObject<HTMLButtonElement | null>;
  closeButtonRef: RefObject<HTMLButtonElement | null>;

  closeDialog: () => void;
  handleNext: () => void;
  handlePrevious: () => void;
  handleKeyDown: (event: KeyboardEvent) => void;

  progressText: string;
  nextButtonText: string;
  nextButtonAriaLabel: string;
  previousButtonAriaLabel: string;
  stepAnnouncement: string;
};

function useEducationalTutorial(config: UseEducationalTutorialConfig = {}): UseEducationalTutorialReturn {
  const { enableKeyboardNavigation = true, enableScreenReaderAnnouncements = true, enableFocusManagement = true, onComplete } = config;
  const t = useTranslations('global_dialogs.educational');

  const [isOpen, setIsOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const steps = t.raw('steps') as EducationalStepsMessages;
  const stepIds = Object.keys(steps);
  const totalSteps = stepIds.length;
  const stepId = stepIds[currentStep - 1] ?? '';
  const stepKey = `steps.${stepId}`;
  const sections = Object.entries(steps[stepId]?.sections ?? {}).map(([sectionId, section]) => ({
    titleKey: section.title ? `${stepKey}.sections.${sectionId}.title` : undefined,
    descriptionKey: `${stepKey}.sections.${sectionId}.description`,
  }));

  const isLastStep = currentStep === totalSteps;
  const isFirstStep = currentStep === 1;

  const closeDialog = () => {
    setIsOpen(false);
    if (onComplete) {
      onComplete();
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      closeDialog();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!enableKeyboardNavigation) return;

    if (event.key === 'Escape') {
      closeDialog();
    } else if (event.key === 'ArrowLeft' && !isFirstStep) {
      handlePrevious();
    } else if (event.key === 'ArrowRight' && !isLastStep) {
      handleNext();
    }
  };

  const progressText = t('accessibility.step_progress', { current: currentStep, total: totalSteps });
  const nextButtonText = isLastStep ? t('actions.done') : t('actions.next');
  const nextButtonAriaLabel = isLastStep ? t('accessibility.complete_tutorial') : t('accessibility.go_to_next', { step: currentStep + 1, total: totalSteps });
  const previousButtonAriaLabel = t('accessibility.go_to_previous', { step: currentStep - 1, total: totalSteps });
  const stepAnnouncement = t('accessibility.step_announcement', {
    current: currentStep,
    total: totalSteps,
    title: t(`${stepKey}.title`),
  });

  useEffect(() => {
    if (isOpen && enableFocusManagement) {
      const timeoutId = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, enableFocusManagement]);

  useEffect(() => {
    if (isOpen && enableScreenReaderAnnouncements && typeof document !== 'undefined') {
      const announcementElement = document.createElement('div');
      announcementElement.setAttribute('aria-live', 'polite');
      announcementElement.setAttribute('aria-atomic', 'true');
      announcementElement.className = 'sr-only';
      announcementElement.textContent = stepAnnouncement;
      document.body.appendChild(announcementElement);

      const timeoutId = setTimeout(() => {
        if (document.body.contains(announcementElement)) {
          document.body.removeChild(announcementElement);
        }
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
        if (document.body.contains(announcementElement)) {
          document.body.removeChild(announcementElement);
        }
      };
    }
  }, [currentStep, isOpen, stepAnnouncement, enableScreenReaderAnnouncements]);

  return {
    isOpen,
    currentStep,
    isLastStep,
    isFirstStep,
    stepKey,
    totalSteps,
    sections,

    nextButtonRef,
    previousButtonRef,
    closeButtonRef,

    closeDialog,
    handleNext,
    handlePrevious,
    handleKeyDown,

    progressText,
    nextButtonText,
    nextButtonAriaLabel,
    previousButtonAriaLabel,
    stepAnnouncement,
  };
}

export { useEducationalTutorial };
export type { EducationalStepSection, UseEducationalTutorialConfig, UseEducationalTutorialReturn };
